import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { apiClient } from '../lib/api';

const FILTERS = [
    { key: 'all', label: 'All trips' },
    { key: 'family', label: 'Family' },
    { key: 'couples', label: 'Couples' },
    { key: 'group', label: 'Group' },
    { key: 'birthday', label: 'Birthday' },
    { key: 'bachelor', label: 'Bachelor/ette' },
    { key: 'singles', label: 'Singles' },
    { key: 'caribbean', label: 'Caribbean' },
    { key: 'alaska', label: 'Alaska' },
    { key: 'mexico', label: 'Mexico' },
];

export default function ReviewsPage() {
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: '', rating: 5, trip: '', text: '', email: '' });

    const load = () => {
        apiClient.get('/reviews').then((r) => setReviews(r.data)).catch(() => {});
    };
    useEffect(load, []);

    const stats = useMemo(() => {
        if (!reviews.length) return { avg: 5, count: 0, fiveStar: 0, fiveStarPct: 100 };
        const total = reviews.reduce((s, r) => s + (r.rating || 0), 0);
        const fiveStar = reviews.filter((r) => r.rating === 5).length;
        return {
            avg: (total / reviews.length).toFixed(1),
            count: reviews.length,
            fiveStar,
            fiveStarPct: Math.round((fiveStar / reviews.length) * 100),
        };
    }, [reviews]);

    const visible = useMemo(() => {
        if (filter === 'all') return reviews;
        return reviews.filter((r) => (`${r.trip || ''} ${r.category || ''}`).toLowerCase().includes(filter));
    }, [reviews, filter]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.text.trim().length < 10) { toast.error('Please write at least 10 characters.'); return; }
        setSubmitting(true);
        try {
            await apiClient.post('/reviews', {
                name: form.name,
                rating: Number(form.rating) || 5,
                trip: form.trip,
                text: form.text,
                email: form.email || undefined,
            });
            toast.success('Thanks for sharing your story — your review is live!');
            setOpen(false);
            setForm({ name: '', rating: 5, trip: '', text: '', email: '' });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not submit review.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16" data-testid="reviews-page">
            {/* HERO */}
            <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-end mb-14">
                <div className="max-w-3xl">
                    <div className="text-xs uppercase tracking-[0.3em] text-brand-aqua mb-3">Voices from the deck</div>
                    <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter">
                        Real trips. <span className="font-serif-italic font-medium text-brand-gold">Real love</span>.
                    </h1>
                    <p className="text-white/70 mt-5 text-base">
                        Every review below is from a real Cruise Vibes guest. Bachelor crews, family reunions, singles cruises, birthday takeovers — read them, then write your own.
                    </p>
                </div>
                <div className="bg-brand-surface border border-brand-gold/30 rounded-2xl p-6 text-center">
                    <div className="flex items-center justify-center gap-1 text-brand-gold mb-2">
                        {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={18} fill="currentColor" />)}
                    </div>
                    <div className="font-display text-5xl font-black text-white">{stats.avg}<span className="text-xl text-white/40">/5</span></div>
                    <div className="text-xs uppercase tracking-widest text-white/55 mt-2" data-testid="reviews-stats">
                        From {stats.count} guests · {stats.fiveStarPct}% five-star
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="share-story-btn" className="btn-sunset rounded-full w-full mt-5 h-10 text-xs uppercase tracking-widest font-semibold">
                                Share your story
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-brand-surface border-white/10 max-w-lg">
                            <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Tell us about your trip</DialogTitle></DialogHeader>
                            <form className="space-y-4" onSubmit={onSubmit}>
                                <Field label="Your name *">
                                    <Input data-testid="review-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-white/15" placeholder="Sarah K." />
                                </Field>
                                <Field label="Rating">
                                    <div className="flex gap-2 mt-1">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                data-testid={`review-rating-${s}`}
                                                onClick={() => setForm({ ...form, rating: s })}
                                                className="hover:scale-110 transition-transform"
                                                aria-label={`Rate ${s} star`}
                                            >
                                                <Star size={28} fill={s <= form.rating ? '#D4AF37' : 'transparent'} className={s <= form.rating ? 'text-brand-gold' : 'text-white/30'} />
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Trip *">
                                    <Input data-testid="review-trip" required value={form.trip} onChange={(e) => setForm({ ...form, trip: e.target.value })} className="bg-transparent border-white/15" placeholder="Birthday Cruise · Caribbean" />
                                </Field>
                                <Field label="Your review *">
                                    <Textarea data-testid="review-text" required rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="bg-transparent border-white/15 resize-none" placeholder="What was unforgettable?" />
                                </Field>
                                <Field label="Email (optional, never shown)">
                                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border-white/15" />
                                </Field>
                                <Button data-testid="review-submit" type="submit" disabled={submitting} className="btn-sunset rounded-full w-full h-11 uppercase tracking-widest font-semibold text-xs">
                                    {submitting ? 'Posting…' : 'Post my review'}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* FILTERS */}
            <div className="flex flex-wrap gap-2 mb-8" data-testid="reviews-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        data-testid={`filter-${f.key}`}
                        onClick={() => setFilter(f.key)}
                        className={`text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${
                            filter === f.key
                                ? 'bg-brand-gold text-brand-bg border-brand-gold font-bold'
                                : 'bg-transparent text-white/65 border-white/15 hover:border-brand-aqua/50 hover:text-white'
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* GRID */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
                {visible.length === 0 && (
                    <div className="col-span-full bg-brand-surface border border-dashed border-white/10 rounded-2xl p-10 text-center text-white/55">
                        No reviews match that filter yet. Be the first.
                    </div>
                )}
                {visible.map((r, i) => (
                    <article
                        key={r.id || i}
                        data-testid={`review-card-${i}`}
                        className="relative bg-brand-surface border border-white/5 rounded-2xl p-6 hover:border-brand-gold/30 transition-colors flex flex-col"
                    >
                        <Quote className="text-brand-gold/15 absolute top-4 right-4" size={42} />
                        <div className="flex items-center gap-1 text-brand-gold mb-3">
                            {Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}
                        </div>
                        <p className="text-sm text-white/85 leading-relaxed flex-1">&ldquo;{r.text}&rdquo;</p>
                        <div className="mt-5 pt-4 border-t border-white/5">
                            <div className="text-sm font-semibold uppercase tracking-tight text-white">{r.name}</div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-aqua mt-1">{r.trip}</div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-white/50">{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
